import {
  Box,
  Flex,
  Link,
  Text,
  TriangleBottomIcon,
  TriangleTopIcon,
} from '@joinrepublic/dls-ui-kit';

import { HOVER_TRANSITION } from '@r-client/republic/ui/offerings';
import { AssetIcon } from '@r-client/wallet/ui/asset';

import type { ICryptoAssetCardProps } from './types';

/** Matches the DLS `extra-small` step, which `createIcon` icons can't name. */
const TREND_ICON_BOX = '4';

/**
 * Reserved for the sparkline. Rendering nothing here rather than collapsing the
 * slot keeps the symbol column at its designed width, so the card doesn't reflow
 * once the chart lands.
 */
// TODO: https://linear.app/republic/issue/USMKT-2166/fe-setup-opportunities-page - render the sparkline
const CHART_SLOT_WIDTH = '16';
const CHART_SLOT_HEIGHT = '10';

/**
 * Compact crypto asset tile for the popular-crypto rail: 248px of logo, ticker,
 * price and 24h change.
 *
 * Figma draws this as a variant of the same `CardRWA` component as
 * {@link ../rwa-card/rwa-card.tsx}, but the two share no layout — a 248x128 data
 * tile against a 692x542 hero card — so they stay separate feature-local
 * components rather than one over-configured one.
 */
export const CryptoAssetCard = ({
  href,
  logoUrl,
  symbol,
  name,
  price,
  change,
  changeDirection,
  onClick,
}: ICryptoAssetCardProps) => {
  const isUp = changeDirection === 'up';
  const ChangeIcon = isUp ? TriangleTopIcon : TriangleBottomIcon;
  const changeColor = isUp ? 'positive.fg-primary' : 'negative.fg-primary';

  return (
    <Link
      href={href}
      isExternal
      onClick={onClick}
      variant="noDecoration"
      display="block"
      p="3"
      borderRadius="lg"
      border="1px solid"
      borderColor="neutral.border-subtle"
      overflow="hidden"
      // The shell is transparent at rest, so it picks up the page background;
      // the tint only needs to appear on hover.
      transition={`background-color ${HOVER_TRANSITION}`}
      _hover={{ bg: 'neutral.fill-subtle' }}
    >
      <Flex
        align="center"
        gap="3"
        pb="3"
        borderBottom="1px solid"
        borderColor="neutral.border-subtle"
      >
        {/* `xl` is the designed 40px circle, and it brings the generic-coin
            fallback for a logo URL that fails to resolve. */}
        <AssetIcon size="xl" logoUrl={logoUrl} tokenName={name} />

        {/* `minWidth="0"` on both the column and its `Text` children is what lets
            `noOfLines` truncate a long name instead of widening the card. */}
        <Box flex="1" minWidth="0">
          <Text size="body-md" fontWeight="medium" noOfLines={1}>
            {symbol}
          </Text>
          <Text size="body-sm-md" variant="text-secondary" noOfLines={1}>
            {name}
          </Text>
        </Box>

        <Box
          flexShrink={0}
          width={CHART_SLOT_WIDTH}
          height={CHART_SLOT_HEIGHT}
        />
      </Flex>

      <Flex align="center" gap="4" mt="3">
        <Box flex="1" minWidth="0">
          <Text size="body-sm-md" variant="text-secondary">
            Price
          </Text>
          <Text size="body-md" fontWeight="medium" noOfLines={1}>
            {price}
          </Text>
        </Box>

        <Box flex="1" minWidth="0" textAlign="right">
          <Text size="body-sm-md" variant="text-secondary">
            Change
          </Text>
          <Flex align="center" justify="flex-end" gap="1">
            {/* Direction is otherwise carried only by the arrow shape and
                colour, neither of which reaches assistive tech. */}
            {/* The design's 12px arrow. These come from `createIcon`, which
                takes no themed `size`, so the 16px box is a style prop — and
                the glyph is inset to 18 of its 24 units, so 16px draws it at
                the designed 12. */}
            <ChangeIcon
              boxSize={TREND_ICON_BOX}
              color={changeColor}
              role="img"
              aria-label={isUp ? 'Up' : 'Down'}
            />
            <Text size="body-md" fontWeight="medium" color={changeColor}>
              {change}
            </Text>
          </Flex>
        </Box>
      </Flex>
    </Link>
  );
};
